import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ASSETS_COSTS_FEATURE_KEY, type SupplierRole } from "@torre/assets";

import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useEntitlements } from "../../../hooks/useEntitlement";
import { activosApi, type AssetFilters } from "../services/activosApi";

// ============================================================================
// Datos de Activos con React Query. Archivo común, idéntico en los dos forks.
// Las llaves llevan la compañía activa: cambiar de compañía no mezcla datos.
// ============================================================================

const ROOT = "activos";
const key = (companyId: string | null | undefined, ...rest: unknown[]) => [ROOT, companyId || "none", ...rest];

function useCompany() {
  return useCompaniesStore((s) => s.companyId);
}

/** El API ya respondió que no hay derecho o compañía: no tiene caso reintentar. */
const retry = (count: number, err: unknown) => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status && status >= 400 && status < 500) return false;
  return count < 2;
};

export function useAssetsSummary() {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "summary"), queryFn: activosApi.summary, staleTime: 60_000, retry });
}

/**
 * ¿Esta sesión ve y captura costos (`feat:activos.costos`, D6)? Lo decide el
 * API: el resumen trae `totalBase` en `null` a quien no tiene la llave (en
 * WAYAK, además, sólo a administradores). Mientras no responde, NO se
 * muestran: sin la llave no aparecen campos ni columnas de costo.
 */
export function useCanSeeCosts(): boolean {
  const { can } = useEntitlements();
  const summary = useAssetsSummary();
  return can(ASSETS_COSTS_FEATURE_KEY) && summary.isSuccess && summary.data?.totalBase != null;
}

export function useAssets(filters: AssetFilters) {
  const companyId = useCompany();
  return useInfiniteQuery({
    queryKey: key(companyId, "list", filters),
    queryFn: ({ pageParam }) => activosApi.list(filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last?.nextCursor ?? null,
    retry,
  });
}

export function useAsset(id: string | null | undefined) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "asset", id), queryFn: () => activosApi.get(id as string), enabled: Boolean(id), retry });
}

export function useAssetEvents(id: string | null | undefined) {
  const companyId = useCompany();
  return useInfiniteQuery({
    queryKey: key(companyId, "events", id),
    queryFn: ({ pageParam }) => activosApi.events(id as string, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last?.nextCursor ?? null,
    enabled: Boolean(id),
    retry,
  });
}

export function useAssetMaintenance(assetId: string | null | undefined) {
  const companyId = useCompany();
  return useQuery({
    queryKey: key(companyId, "maintenance", assetId),
    queryFn: () => activosApi.maintenance(assetId as string),
    enabled: Boolean(assetId),
    retry,
  });
}

export function usePurchases(supplierId?: string) {
  const companyId = useCompany();
  return useInfiniteQuery({
    queryKey: key(companyId, "purchases", supplierId || "all"),
    queryFn: ({ pageParam }) => activosApi.purchases(supplierId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last?.nextCursor ?? null,
    retry,
  });
}

export function usePurchase(id: string | null | undefined) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "purchase", id), queryFn: () => activosApi.purchase(id as string), enabled: Boolean(id), retry });
}

export function useSuppliers(params: { role?: SupplierRole | ""; q?: string } = {}, enabled = true) {
  const companyId = useCompany();
  return useQuery({
    queryKey: key(companyId, "suppliers", params),
    queryFn: () => activosApi.suppliers(params),
    staleTime: 60_000,
    enabled,
    retry,
  });
}

/** Personas, unidades y bases: sólo se referencian. Si la sesión no las puede leer, la lista queda vacía. */
export function usePeople(enabled = true) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "people"), queryFn: activosApi.people, staleTime: 5 * 60_000, enabled, retry: false });
}

export function useVehicles(enabled = true) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "vehicles"), queryFn: activosApi.vehicles, staleTime: 5 * 60_000, enabled, retry: false });
}

export function useBases(enabled = true) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "bases"), queryFn: activosApi.bases, staleTime: 5 * 60_000, enabled, retry: false });
}

/** Mutación que, al terminar, invalida todo lo de Activos de la compañía activa. */
export function useActivosMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  const companyId = useCompany();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: [ROOT, companyId || "none"] }),
  });
}
